import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircle, PaperPlaneTilt, Trash, Paperclip, DownloadSimple, X } from "@phosphor-icons/react";
import useDraft from "@/hooks/useDraft";
import { LinkifiedText } from "@/components/LinkifiedText";

/**
 * TaskNotes — collaborative notes thread on a single checklist task.
 *
 * Props:
 *   - taskId: the task's task_id (required)
 *   - clientUserId: optional. If present, we're on the admin side and will
 *     use /admin/clients/{clientUserId}/tasks/{taskId}/notes endpoints with
 *     admin delete + email-to-client behavior. If omitted, we're the client
 *     and will use /me/tasks/{taskId}/notes.
 *   - initialNotes: array of notes already attached to the task (optional).
 *   - testid: optional data-testid prefix.
 */
async function downloadNoteFile(f) {
  try {
    const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = f.filename || "file"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
}

export default function TaskNotes({ taskId, clientUserId, initialNotes = [],
                                    testid = "task-notes", basePath = null,
                                    canDelete = null, role = null, anchorId = null,
                                    label = null, uploadPath = null }) {
  const isAdmin = role ? role === "admin" : (basePath ? true : !!clientUserId);
  const [notes, setNotes] = useState(initialNotes);
  const [open, setOpen] = useState(!!anchorId && window.location.hash === `#${anchorId}`);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const fileRef = useRef(null);
  const [draft, setDraft, { saved: draftSaved, clear: clearDraft }] = useDraft(
    `tasknote.${isAdmin ? "a" : "c"}.${taskId}`, { body: "" }
  );

  // Keep local state in sync when parent passes a refreshed list
  useEffect(() => { setNotes(initialNotes); }, [initialNotes]);

  const base = basePath
    || (isAdmin ? `/admin/clients/${clientUserId}/tasks/${taskId}/notes`
                : `/me/tasks/${taskId}/notes`);

  const refresh = async () => {
    // Try to GET only when basePath or clientUserId is set (both support GET)
    try {
      const { data } = await api.get(base);
      setNotes(data);
    } catch (_e) { /* non-blocking */ }
  };

  const uploadAll = async () => {
    const ids = [];
    for (const f of picked) {
      const fd = new FormData();
      fd.append("file", f);
      if (isAdmin && uploadPath.startsWith("/admin/clients/")) { fd.append("tag", "note"); fd.append("note", "Note attachment"); }
      if (uploadPath.startsWith("/admin-tasks/")) fd.append("for_note", "true");
      const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      ids.push(data.file_id);
    }
    return ids;
  };

  const add = async (e) => {
    e?.preventDefault?.();
    if (!draft.body.trim() && !picked.length) return;
    setBusy(true);
    try {
      const file_ids = picked.length ? await uploadAll() : [];
      const { data } = await api.post(base, { content: draft.body, file_ids });
      setNotes((prev) => [...prev, data]);
      clearDraft();
      setPicked([]);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(isAdmin ? "Note added — client notified" : "Note added — team notified");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (noteId) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`${base}/${noteId}`);
      setNotes((prev) => prev.filter(n => n.note_id !== noteId));
    } catch (_e) { toast.error("Failed"); }
  };

  const count = notes.length;
  return (
    <div className="mt-3" data-testid={`${testid}-${taskId}`}>
      <button type="button"
        onClick={() => { setOpen(o => !o); if (!open) refresh(); }}
        data-testid={`${testid}-toggle-${taskId}`}
        className="btn-ghost text-xs">
        <ChatCircle size={12} weight="bold" />
        {count > 0 ? `${count} note${count === 1 ? "" : "s"}` : (label || "Add note")}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {notes.length > 0 && (
            <div className="space-y-2">
              {notes.map(n => {
                const isAdminNote = n.author_role === "admin";
                return (
                <div key={n.note_id}
                  data-testid={`${testid}-item-${n.note_id}`}
                  className="hair p-3 text-sm"
                  style={{
                    background: "var(--panel-bg, var(--bg))",
                    borderLeft: `3px solid ${isAdminNote ? "#3b82f6" : "#d97706"}`,
                    color: "var(--text)",
                  }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="label text-[10px]"
                        style={{ color: isAdminNote ? "#3b82f6" : "#d97706", fontWeight: 700 }}>
                        {isAdminNote ? "Agency" : "Client"} ·{" "}
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                      <p className="mt-1 whitespace-pre-line leading-relaxed"
                        style={{ color: "var(--text)" }}><LinkifiedText text={n.content} /></p>
                      {(n.files || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {n.files.map(f => (
                            <button key={f.file_id} type="button" onClick={() => downloadNoteFile(f)}
                              data-testid={`${testid}-file-${f.file_id}`}
                              className="btn-ghost text-[11px] h-7" title={f.filename}>
                              <DownloadSimple size={11} weight="bold" />
                              <span className="truncate max-w-[180px]">{f.filename}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {isAdmin && (
                      <button type="button" onClick={() => del(n.note_id)}
                        data-testid={`${testid}-del-${n.note_id}`}
                        className="shrink-0 p-1 hover:opacity-70"
                        style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}
                        title="Delete note">
                        <Trash size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <form onSubmit={add} className="space-y-2">
            <textarea rows={2} value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              data-testid={`${testid}-body-${taskId}`}
              placeholder={isAdmin ? "Add a note the client will see…" : "Add a note the team will see…"}
              className="w-full px-3 py-2 text-sm" />
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid={`${testid}-picked-${taskId}`}>
                {picked.map((f, i) => (
                  <span key={i} className="sample-badge flex items-center gap-1">
                    {f.name}
                    <button type="button" onClick={() => setPicked(picked.filter((_, j) => j !== i))}
                      data-testid={`${testid}-remove-${taskId}-${i}`} className="-m-1 p-1.5"
                      aria-label={`Remove ${f.name}`}><X size={12} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center flex-wrap">
              {uploadPath && (
                <>
                  <input ref={fileRef} type="file" multiple className="hidden"
                    data-testid={`${testid}-file-input-${taskId}`}
                    onChange={(e) => { const add = Array.from(e.target.files || []); e.target.value = ""; setPicked(prev => [...prev, ...add.filter(f => !prev.some(p => p.name === f.name && p.size === f.size))]); }} />
                  <button type="button" onClick={() => fileRef.current?.click()}
                    data-testid={`${testid}-attach-${taskId}`} className="btn-ghost text-xs h-9">
                    <Paperclip size={11} weight="bold" /> Attach files
                  </button>
                </>
              )}
              <button type="submit" disabled={busy || (!draft.body.trim() && !picked.length)}
                data-testid={`${testid}-send-${taskId}`}
                className="btn-primary text-xs h-9">
                <PaperPlaneTilt size={11} weight="bold" />
                {busy ? "Sending…" : isAdmin ? "Add & notify client" : "Add & notify team"}
              </button>
              {draftSaved && (
                <span className="label text-[10px]" style={{ color: "var(--text-dim)" }}>
                  Draft saved
                </span>
              )}
              {draft.body && (
                <button type="button" onClick={clearDraft}
                  data-testid={`${testid}-clear-${taskId}`}
                  className="btn-ghost text-[10px]">Clear</button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
